import React, { useMemo } from 'react';
import { Box, Typography, Paper, Skeleton, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';

/** Format credits as call duration: 1 credit = 1 minute → "3h 20m", "45m", "30s" */
function formatCallDuration(credits) {
  const totalSeconds = Math.floor(Number(credits) || 0) * 60;
  if (totalSeconds >= 3600) {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  if (totalSeconds >= 60) return `${Math.floor(totalSeconds / 60)}m`;
  return `${totalSeconds}s`;
}

export default function CreditsUsageChart({ data, loading, dateFilterLabel }) {
  const theme = useTheme();
  const barColor = theme.palette.primary.main;

  const chartData = useMemo(() => {
    const raw = data || [];
    return raw.map((d) => ({
      ...d,
      used: d.used ?? 0,
      duration_display: d.duration_display ?? formatCallDuration(d.used ?? 0),
    }));
  }, [data]);

  const todayPeriod = format(new Date(), 'MMM d');
  const lastPeriod = chartData.length ? chartData[chartData.length - 1]?.period : null;
  const showTodayLine = lastPeriod === todayPeriod;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length || !label) return null;
    const used = payload[0]?.value ?? 0;
    const duration = payload[0]?.payload?.duration_display ?? formatCallDuration(used);
    return (
      <Paper elevation={2} sx={{ p: 1.5, minWidth: 140 }}>
        <Typography variant="caption" color="text.secondary" display="block">Date: {label}</Typography>
        <Typography variant="body2" fontWeight={600}>Credits used: {used}</Typography>
        <Typography variant="body2" color="text.secondary">Call duration: {duration}</Typography>
      </Paper>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Credit usage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dateFilterLabel ?? 'Credits used per day (1 credit = 1 min call) — select date range above'}
        </Typography>
      </Box>
      {loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
      ) : (
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                angle={-35}
                textAnchor="end"
                height={50}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                allowDecimals={false}
                label={{ value: 'Credits (per day)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                width={50}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              {showTodayLine && (
                <ReferenceLine
                  x={todayPeriod}
                  stroke="#64748b"
                  strokeDasharray="4 4"
                  label={{ value: 'Today', position: 'top', fontSize: 10 }}
                />
              )}
              <Bar
                dataKey="used"
                name="Credits used"
                fill={barColor}
                stroke={barColor}
                strokeWidth={1}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
      {!loading && chartData.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 1, bgcolor: barColor }} />
          <Typography variant="caption" color="text.secondary">
            Credits used (1 credit = 1 min call)
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
